import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw, RotateCcw, Search, PlayCircle } from 'lucide-react';
import { getAuthHeader } from '../services/tokenService';
import StripeDossierLien from './StripeDossierLien';
import StripeRepriseMandat from './StripeRepriseMandat';
import MandatControleStripe from './MandatControleStripe';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/webhook/stripe/admin`;
const STATUTS = [['', 'Tous'], ['processed', 'Traités'], ['failed', 'En échec'], ['ignored', 'Ignorés'], ['processing', 'En cours'], ['a_verifier', 'À vérifier']];
const COULEUR = { processed: 'bg-emerald-100 text-emerald-700', failed: 'bg-red-100 text-red-700', ignored: 'bg-slate-100 text-slate-600', processing: 'bg-amber-100 text-amber-700', a_verifier: 'bg-violet-100 text-violet-700' };
const LIB = { processed: 'Traité', failed: 'En échec', ignored: 'Ignoré', processing: 'En cours', a_verifier: 'À vérifier (ancien registre)' };
const ETATS = { a_traiter: 'À traiter', deja_traite: 'Déjà traité', deja_traite_ancien_recepteur: 'Déjà traité (ancien)', deja_encaisse_en_base: 'Déjà en base', sans_effet: 'Sans effet', a_verifier: 'À vérifier', ancien_registre: 'Ancien registre', deja_applique_en_base: 'Déjà appliqué en base', reprise_necessaire: 'Reprise nécessaire', anomalie: 'Anomalie à résoudre' };
const fmt = (d) => (d ? new Date(d).toLocaleString('fr-FR') : '—');

function Rattrapage({ onDone }) {
  const [depuis, setDepuis] = useState(new Date(Date.now() - 7 * 864e5).toISOString().slice(0, 10));
  const [rapport, setRapport] = useState(null);
  const [busy, setBusy] = useState(false);
  const lancer = async (appliquer) => {
    if (appliquer && !window.confirm(`Appliquer les ${rapport?.synthese?.a_traiter || 0} notification(s) « À traiter » ?\nLes opérations déjà enregistrées ne seront pas recréées.`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/rattrapage`, null, { params: { depuis, appliquer }, headers: getAuthHeader() });
      setRapport(r.data);
      toast.success(appliquer ? `${r.data.appliques} notification(s) appliquée(s)` : `${r.data.total} notification(s) Stripe analysée(s)`);
      if (appliquer) onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Rattrapage impossible'); }
    setBusy(false);
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="stripe-rattrapage">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <p className="font-semibold text-slate-800">Rattrapage des notifications manquées</p>
          <p className="text-xs text-slate-500">Compare Stripe (30 derniers jours max) avec l'application. Le rapport ne modifie rien.</p>
        </div>
        <input type="date" value={depuis} onChange={(e) => setDepuis(e.target.value)} className="ml-auto border rounded-lg px-2 py-1 text-sm" data-testid="stripe-rattrapage-date" />
        <button type="button" disabled={busy} onClick={() => lancer(false)} className="inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm hover:bg-slate-50" data-testid="stripe-rattrapage-rapport">
          <Search className="h-4 w-4" /> Rapport
        </button>
        <button type="button" disabled={busy || !rapport?.synthese?.a_traiter} onClick={() => lancer(true)} className="inline-flex items-center gap-1 rounded-lg bg-orange-600 text-white px-3 py-1.5 text-sm disabled:opacity-40" data-testid="stripe-rattrapage-appliquer">
          <PlayCircle className="h-4 w-4" /> Appliquer
        </button>
      </div>
      {rapport && <RapportTable rapport={rapport} onReload={() => lancer(false)} />}
    </div>
  );
}

const ETAT_COUL = { reprise_necessaire: 'text-orange-700', anomalie: 'text-red-700', deja_applique_en_base: 'text-emerald-700', a_traiter: 'text-orange-700' };

function RapportTable({ rapport, onReload }) {
  const action = async (url, params, question) => {
    if (!window.confirm(question)) return;
    try {
      const r = await axios.post(url, null, { params, headers: getAuthHeader() });
      toast.success(r.data?.second_traitement ? `Reprise : ${r.data.premier_traitement?.status} · 2e passage : ${r.data.second_traitement}` : 'Blocage enregistré');
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action impossible'); }
  };
  return (
    <div data-testid="stripe-rattrapage-resultat">
      <p className="text-sm text-slate-700 mb-2">
        {rapport.total} notification(s) depuis le {String(rapport.depuis).split('-').reverse().join('/')} 00:00 (heure de Guadeloupe) —{' '}
        {Object.entries(rapport.synthese || {}).map(([k, v]) => `${ETATS[k] || k} : ${v}`).join(' · ')}
        {rapport.mode === 'application' && ` — ${rapport.appliques} appliquée(s)`}
      </p>
      <div className="max-h-96 overflow-auto text-xs border rounded-lg">
        <table className="w-full">
          <tbody>
            {rapport.evenements.filter((e) => e.etat !== 'sans_effet' || e.type === 'setup_intent.succeeded').map((e) => (
              <tr key={e.event_id} className="border-b last:border-0 align-top" data-testid={`rapport-ligne-${e.event_id}`}>
                <td className="p-1.5 font-mono">{e.event_id}<div className="text-slate-400">{e.checkout_id || ''} {e.setup_intent_id || ''}</div>{e.lie_a && <div className="text-slate-500">lié à {e.lie_a}</div>}</td>
                <td className="p-1.5">{e.type}<div className="text-slate-500">{e.metadata_type || '—'}</div></td>
                <td className="p-1.5">{fmt(e.cree_le)}</td>
                <td className="p-1.5">{e.souscription_id || e.mandat_id || '—'}<div className="text-slate-500">{e.sepa_plan_id || ''}</div></td>
                <td className="p-1.5">{e.mandat_etat || '—'}{e.blocage !== undefined && <div className={e.blocage ? 'text-emerald-700' : 'text-red-700'}>{e.blocage ? 'bloqué' : 'non bloqué'}</div>}{e.echeances_dues?.length > 0 && <div className="text-red-700">{e.echeances_dues.length} échéance(s) due(s)</div>}</td>
                <td className={`p-1.5 ${ETAT_COUL[e.etat] || ''}`}>{e.application || ETATS[e.etat] || e.etat}<div className="text-slate-500">{e.motif}</div></td>
                <td className="p-1.5 whitespace-nowrap space-y-1">
                  {e.mandat_id && e.blocage === false && <button type="button" className="block rounded border border-red-300 text-red-700 px-2 py-0.5" data-testid={`rapport-bloquer-${e.event_id}`} onClick={() => action(`${process.env.REACT_APP_BACKEND_URL}/api/sepa-stripe/admin/mandats/${e.mandat_id}/recovery-hold`, { actif: true, motif: 'blocage_conservatoire_verification_stripe' }, 'Bloquer les prélèvements automatiques de ce mandat ?')}>Bloquer le mandat</button>}
                  {e.mandat_id && <MandatControleStripe mandatId={e.mandat_id} />}
                  {e.etat === 'reprise_necessaire' && <button type="button" className="block rounded border border-orange-300 text-orange-700 px-2 py-0.5" data-testid={`rapport-reprendre-${e.event_id}`} onClick={() => action(`${API}/evenements/${e.event_id}/reprendre`, {}, 'Reprendre cette seule notification ? Le mandat activé restera bloqué, aucun paiement ne sera créé.')}>Reprendre</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function StripeNotificationsAdmin() {
  const [statut, setStatut] = useState('');
  const [data, setData] = useState({ evenements: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/evenements`, { params: { status: statut, limit: 100 }, headers: getAuthHeader() });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Chargement impossible'); }
    setLoading(false);
  }, [statut]);
  useEffect(() => { charger(); }, [charger]);
  const rejouer = async (id, statutEvt) => {
    if (statutEvt === 'a_verifier' && !window.confirm("Ancien traitement non vérifiable. Lancer la reprise contrôlée ?\nLes effets déjà enregistrés ne sont pas recréés par les traitements métier.")) return;
    try {
      const r = await axios.post(`${API}/evenements/${id}/rejouer`, null, { headers: getAuthHeader() });
      toast.success(`Résultat : ${r.data.statut}${r.data.resultat ? ` — ${r.data.resultat}` : ''}`);
      charger();
    } catch (e) { toast.error(e.response?.data?.detail || 'Rejeu impossible'); }
  };
  return (
    <div className="space-y-4" data-testid="stripe-notifications-admin">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-lg font-semibold text-slate-800 mr-2">Notifications Stripe reçues</h2>
        {STATUTS.map(([v, l]) => (
          <button key={v || 'tous'} type="button" onClick={() => setStatut(v)} data-testid={`stripe-filtre-${v || 'tous'}`}
            className={`px-3 py-1 rounded-full text-xs border transition-colors ${statut === v ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 hover:border-slate-400'}`}>{l}</button>
        ))}
        <span className="text-xs text-slate-500" data-testid="stripe-notifications-total">{data.total} au total</span>
        <button type="button" onClick={charger} className="ml-auto p-2 rounded-lg hover:bg-slate-100" title="Actualiser" data-testid="stripe-notifications-actualiser">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      <Rattrapage onDone={charger} />
      <StripeRepriseMandat />
      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs text-left">
            <tr><th className="p-2">Reçu le</th><th className="p-2">Événement</th><th className="p-2">Type</th><th className="p-2">Dispositif</th>
              <th className="p-2">État</th><th className="p-2">Tent.</th><th className="p-2">Résultat / erreur</th><th className="p-2" /></tr>
          </thead>
          <tbody>
            {data.evenements.length === 0 && (
              <tr><td colSpan={8} className="p-6 text-center text-slate-400" data-testid="stripe-notifications-vide">Aucune notification reçue pour ce filtre.</td></tr>
            )}
            {data.evenements.map((e) => (
              <tr key={e.event_id} className="border-t" data-testid={`stripe-evt-${e.event_id}`}>
                <td className="p-2 whitespace-nowrap">{fmt(e.received_at)}</td>
                <td className="p-2 font-mono text-xs">{e.event_id}{!e.livemode && <span className="ml-1 text-amber-600">(test)</span>}</td>
                <td className="p-2 text-xs">{e.type}</td>
                <td className="p-2 text-xs">{e.metadata_type || '—'}</td>
                <td className="p-2"><span className={`px-2 py-0.5 rounded-full text-xs ${COULEUR[e.status] || ''}`}>{LIB[e.status] || e.status}</span></td>
                <td className="p-2 text-xs">{e.tentatives || 1}{e.doublons_recus ? ` (+${e.doublons_recus} renvoi)` : ''}</td>
                <td className="p-2 text-xs text-slate-600 max-w-xs truncate" title={e.erreur || e.resultat}>{e.erreur || e.resultat || '—'}</td>
                <td className="p-2 whitespace-nowrap space-x-1">
                  <StripeDossierLien eventId={e.event_id} />
                  {['failed', 'processing', 'a_verifier'].includes(e.status) && (
                    <button type="button" onClick={() => rejouer(e.event_id, e.status)} className="inline-flex items-center gap-1 rounded-lg border border-orange-300 text-orange-700 px-2 py-1 text-xs hover:bg-orange-50" data-testid={`stripe-rejouer-${e.event_id}`}>
                      <RotateCcw className="h-3.5 w-3.5" /> Rejouer
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
